import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { GoogleMap } from "@react-google-maps/api";
import { useGoogleMaps } from "../../config/google-maps-loader";
import { useSocket } from "../../context/socket-context";
import apiUrls from "../../config/api-urls";
import tokenService from "../../services/token-service";
import toast from "react-hot-toast";
import {
  Phone,
  MessageCircle,
  Clock,
  MapPin,
  Loader2,
  Star,
  XCircle,
  Navigation,
  CheckCircle,
  AlertTriangle
} from "lucide-react";
import Chat from "../../components/customer/schedule/chat";
import DisputeDialog from "../../components/handyman/modals/dispute-dialog";

const LiveHandymanTracking = ({ task: taskProp, serviceRequestId: serviceRequestIdProp }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const lastCancellationHandledRef = useRef(null);

  const handymanFromState = location?.state?.handyman;

  // Get taskId from multiple sources
  const taskIdFromProps = serviceRequestIdProp || taskProp?._id || taskProp?.id;
  const taskIdFromState = location?.state?.serviceRequestId || location?.state?.task?._id || location?.state?.task?.id;
  const initialTaskId = taskIdFromProps || taskIdFromState;
  const [taskId, setTaskId] = useState(initialTaskId);

  useEffect(() => {
    const nextTaskId = taskIdFromProps || taskIdFromState;
    if (nextTaskId && nextTaskId !== taskId) {
      setTaskId(nextTaskId);
    }
  }, [taskIdFromProps, taskIdFromState, taskId]);

  const [handyman, setHandyman] = useState(null);
  const [liveTask, setLiveTask] = useState(null);

  const { isLoaded } = useGoogleMaps();
  const mapRef = useRef(null);
  const handymanMarkerRef = useRef(null);
  const customerMarkerRef = useRef(null);

  // Use Socket Context
  const {
    socket,
    handymanLocation: socketHandymanLocation,
    jobStatus: socketJobStatus,
    handymanEta: socketEta,
    joinTaskRoom,
    leaveTaskRoom,
    isConnected
  } = useSocket();

  const [eta, setEta] = useState(null);
  const [distance, setDistance] = useState(null);
  const [status, setStatus] = useState("assigned");
  const [currentHandymanPos, setCurrentHandymanPos] = useState(null);
  const [showApproveForm, setShowApproveForm] = useState(false);
  const [isApprovingCompletion, setIsApprovingCompletion] = useState(false);
  const [approveError, setApproveError] = useState("");
  const [showCompletionForm, setShowCompletionForm] = useState(false);
  const [rating, setRating] = useState(5);
  const [review, setReview] = useState("");
  const [isSubmittingCompletion, setIsSubmittingCompletion] = useState(false);
  const [completionError, setCompletionError] = useState("");
  const [showCancelForm, setShowCancelForm] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelError, setCancelError] = useState("");
  const [isChatVisible, setIsChatVisible] = useState(false);
  const [isLoadingTask, setIsLoadingTask] = useState(false);
  const [taskLoadError, setTaskLoadError] = useState("");
  const [showDisputeDialog, setShowDisputeDialog] = useState(false);

  useEffect(() => {
    if (!handyman && handymanFromState) {
      setHandyman(handymanFromState);
    }
  }, [handyman, handymanFromState]);

  const effectiveHandyman =
    handyman ||
    handymanFromState ||
    (socketJobStatus && typeof socketJobStatus === "object"
      ? {
          _id: socketJobStatus.handymanId,
          name: socketJobStatus.handymanName,
          fullName: socketJobStatus.handymanName,
        }
      : null);

  const displayTask = liveTask;
  const displayHandyman = effectiveHandyman;

  // Safely extract coordinates with fallback values (always executed, even if task/handyman is null)
  const customerPos = {
    lat: displayTask?.location?.coordinates?.[1] || displayTask?.latitude || 31.5204,
    lng: displayTask?.location?.coordinates?.[0] || displayTask?.longitude || 74.3587,
  };

  const handymanInitialPos = {
    lat: displayHandyman?.location?.coordinates?.[1] || displayHandyman?.latitude || 31.5204,
    lng: displayHandyman?.location?.coordinates?.[0] || displayHandyman?.longitude || 74.3587,
  };

  useEffect(() => {
    const fetchTask = async () => {
      if (!taskId) return;
      setIsLoadingTask(true);
      setTaskLoadError("");

      try {
        const token = tokenService.getToken();
        const res = await fetch(`${apiUrls.services.getCustomerTaskById}/${taskId}`, {
          method: "GET",
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data?.message || data?.error?.message || "Failed to load task");
        }

        const freshTask = data?.data;
        if (freshTask) {
          setLiveTask(freshTask);
          if (freshTask?.status) setStatus(freshTask.status);

          // Map handyman from API response
          if (freshTask?.handyman) {
            setHandyman({
              _id: freshTask.handyman.id,
              name: freshTask.handyman.name,
              fullName: freshTask.handyman.name,
              phoneNumber: freshTask.handyman.phone,
              phone: freshTask.handyman.phone,
              profileImage: freshTask.handyman.profileImage,
              rating: freshTask.handyman.rating,
              averageRating: freshTask.handyman.rating,
              ratingCount: freshTask.handyman.ratingCount,
              location: freshTask.handyman.location,
              latitude: freshTask.handyman.location?.coordinates?.[1],
              longitude: freshTask.handyman.location?.coordinates?.[0],
            });
          }
        }
      } catch (error) {
        setTaskLoadError(error?.message || "Unable to load task");
      } finally {
        setIsLoadingTask(false);
      }
    };

    fetchTask();
  }, [taskId]);

  // Join task room on mount and leave on unmount
  useEffect(() => {
    if (taskId && isConnected) {
      joinTaskRoom(taskId);
    }
    return () => {
      if (taskId && leaveTaskRoom) {
        leaveTaskRoom(taskId);
      }
    };
  }, [taskId, isConnected]);

  // Listen for dispute created event
  useEffect(() => {
    if (!socket) return;

    const handleDisputeCreated = (data) => {
      if (data.disputeId) {
        setStatus("disputed");
        toast.error(`Dispute raised: ${data.reason}`);
        
        setTimeout(() => {
          navigate("/customer/jobs");
        }, 1500);
      }
    };

    socket.on('dispute:created', handleDisputeCreated);

    return () => {
      socket.off('dispute:created', handleDisputeCreated);
    };
  }, [socket, navigate]);

  // Update handyman position from socket
  useEffect(() => {
    if (socketHandymanLocation) {
      const pos = {
        lat: socketHandymanLocation.location?.lat || socketHandymanLocation.lat,
        lng: socketHandymanLocation.location?.lng || socketHandymanLocation.lng,
      };
      setCurrentHandymanPos(pos);

      if (handymanMarkerRef.current) {
        handymanMarkerRef.current.position = pos;
      }

      if (mapRef.current) {
        mapRef.current.panTo(pos);
      }

      calculateETA(pos);

      // Let handyman control status explicitly - no auto-update
    }
  }, [socketHandymanLocation]);

  // Update status from socket
  useEffect(() => {
    if (socketJobStatus) {
      if (typeof socketJobStatus === "string") {
        setStatus(socketJobStatus);
        return;
      }

      if (!handyman && socketJobStatus?.handymanId && socketJobStatus?.handymanName) {
        setHandyman({
          _id: socketJobStatus.handymanId,
          name: socketJobStatus.handymanName,
          fullName: socketJobStatus.handymanName,
        });
      }

      const incomingTaskId = socketJobStatus?.taskId || socketJobStatus?.serviceRequestId;
      if (incomingTaskId && incomingTaskId !== taskId) {
        setLiveTask(null);
        setHandyman(null);
        setTaskLoadError("");
        setTaskId(incomingTaskId);
        return;
      }

      setStatus(socketJobStatus.status || socketJobStatus);
      setLiveTask((prev) => ({
        ...(prev || {}),
        ...socketJobStatus,
        completedWork: socketJobStatus?.completedWork || prev?.completedWork,
        paymentReceived:
          socketJobStatus?.status === "payment_received"
            ? {
                ...(prev?.paymentReceived || {}),
                confirmedByHandyman: true,
                confirmedAt: socketJobStatus?.timestamp || new Date().toISOString(),
              }
            : socketJobStatus?.paymentReceived || prev?.paymentReceived,
      }));

      if (socketJobStatus?.awaitingConfirmation && socketJobStatus?.finalPrice) {
        const price = socketJobStatus.finalPrice;
      }

      if (
        socketJobStatus?.status === "payment_received" ||
        socketJobStatus?.paymentReceived ||
        socketJobStatus?.paymentReceived?.confirmedByHandyman
      ) {
        const price = socketJobStatus?.finalPrice ?? socketJobStatus?.offerPrice ?? socketJobStatus?.price;
        toast.success(
          socketJobStatus?.message ||
            `Payment received${price != null ? `: PKR ${Number(price).toLocaleString()}` : ""}`
        );
      }

      if ((socketJobStatus?.status || "") === "cancelled") {
        const incomingTaskId = socketJobStatus?.taskId || socketJobStatus?.serviceRequestId || taskId;
        const reason = (socketJobStatus?.reason || "").trim();
        const cancelledBy = socketJobStatus?.cancelledBy;
        const eventKey = `${incomingTaskId || ""}:${cancelledBy || ""}:${reason || ""}:${socketJobStatus?.timestamp || ""}`;

        if (lastCancellationHandledRef.current !== eventKey) {
          lastCancellationHandledRef.current = eventKey;
          const message =
            socketJobStatus?.message ||
            (cancelledBy === "handyman"
              ? `Handyman cancelled the request${reason ? `: ${reason}` : "."}`
              : cancelledBy === "customer"
                ? `You cancelled the request${reason ? `: ${reason}` : "."}`
                : `Job cancelled${reason ? `: ${reason}` : "."}`);
          toast(message);
          setTimeout(() => {
            navigate("/customer/jobs");
          }, 1500);
        }
      }
    }
  }, [socketJobStatus, taskId]);

  const paymentConfirmed =
    status === "payment_received" ||
    liveTask?.paymentReceived?.confirmedByHandyman === true;

  // Update ETA from socket (real-time from handyman)
  useEffect(() => {
    if (socketEta) {
      if (socketEta.eta) {
        setEta(socketEta.eta);
      }
      if (socketEta.distanceKm !== undefined) {
        setDistance(`${socketEta.distanceKm.toFixed(1)} km`);
      }
    }
  }, [socketEta]);

  const workProof =
    liveTask?.workProof ||
    liveTask?.completedWork ||
    liveTask?.completionDetails ||
    liveTask?.workCompletion ||
    {};

  const workDescription =
    workProof?.description || liveTask?.description || "";

  const workPictures =
    workProof?.pictures || [];

  const workVideo = workProof?.video || null;

  const approveCompletion = async () => {
    if (!taskId) {
      setApproveError("Task id missing. Please reopen the job.");
      return;
    }

    setIsApprovingCompletion(true);
    setApproveError("");

    try {
      const token = tokenService.getToken();
      const res = await fetch(apiUrls.services.approveCompletion, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ taskId }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(
          data?.error?.message || data?.message || "Failed to approve completion"
        );
      }

      setStatus("approved");
      setShowApproveForm(false);
      toast.success("Approved! You can now confirm & rate.");
    } catch (error) {
      const message = error?.message || "Unable to approve completion";
      setApproveError(message);
      toast.error(message);
    } finally {
      setIsApprovingCompletion(false);
    }
  };

  const submitCompletion = async () => {
    if (!taskId) {
      setCompletionError("Task id missing. Please reopen the job.");
      return;
    }

    setIsSubmittingCompletion(true);
    setCompletionError("");

    try {
      const token = tokenService.getToken();
      const res = await fetch(apiUrls.services.confirmCompletion, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          taskId,
          rating,
          review,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(
          data?.error?.message || data?.message || "Failed to mark completed"
        );
      }

      setStatus("completed");
      setShowCompletionForm(false);
      toast.success("Thanks! Your feedback has been submitted.");
      
      // Navigate to service page after successful completion
      setTimeout(() => {
        navigate('/services');
      }, 2000);
    } catch (error) {
      const message = error?.message || "Unable to submit completion";
      setCompletionError(message);
      toast.error(message);
    } finally {
      setIsSubmittingCompletion(false);
    }
  };

  const submitCancellation = async () => {
    if (!taskId) {
      setCancelError("Task id missing. Please reopen the job.");
      return;
    }
    if (!cancelReason.trim()) {
      setCancelError("Please share a reason for cancellation.");
      return;
    }

    setIsCancelling(true);
    setCancelError("");

    try {
      const token = tokenService.getToken();
      const res = await fetch(apiUrls.services.cancelServiceRequest, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          taskId,
          reason: cancelReason,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(
          data?.error?.message || data?.message || "Failed to cancel request"
        );
      }

      setStatus("cancelled");
      setShowCancelForm(false);
      toast.success("Service request cancelled");

      setTimeout(() => {
        navigate("/customer/jobs");
      }, 800);
    } catch (error) {
      const message = error?.message || "Unable to cancel request";
      setCancelError(message);
      toast.error(message);
    } finally {
      setIsCancelling(false);
    }
  };

  // INITIAL CUSTOMER + HANDYMAN MARKERS WITH ADVANCED MARKER ELEMENT
  // -------------------------------------------------------------
  useEffect(() => {
    if (!isLoaded || !mapRef.current || !liveTask || !displayHandyman) return;
    if (!window.google?.maps?.marker?.AdvancedMarkerElement) {
      return;
    }

    const { AdvancedMarkerElement, PinElement } = window.google.maps.marker;

    // Customer marker (Blue pin)
    const customerPinElement = new PinElement({
      background: "#4285F4", // Blue
      borderColor: "#3367D6",
      glyphColor: "#FFFFFF",
    });

    customerMarkerRef.current = new AdvancedMarkerElement({
      position: customerPos,
      map: mapRef.current,
      title: "Your Location",
      content: customerPinElement.element,
    });

    // Handyman marker (Green pin)
    const handymanPinElement = new PinElement({
      background: "#34A853", // Green
      borderColor: "#2E8B47",
      glyphColor: "#FFFFFF",
    });

    const initialPos = currentHandymanPos || handymanInitialPos;
    handymanMarkerRef.current = new AdvancedMarkerElement({
      position: initialPos,
      map: mapRef.current,
      title: `${displayHandyman?.name || 'Handyman'} - En Route`,
      content: handymanPinElement.element,
    });

    calculateETA(initialPos);
  }, [isLoaded, liveTask, displayHandyman]);

  // Calculate ETA on mount even if markers don't load
  useEffect(() => {
    if (liveTask && (displayHandyman || currentHandymanPos || socketHandymanLocation)) {
      const hPos = currentHandymanPos || handymanInitialPos;
      console.log('🚀 Initial ETA calculation triggered with position:', hPos);
      // Small delay to ensure component is mounted
      const timer = setTimeout(() => {
        calculateETA(hPos);
      }, 500);
      return () => clearTimeout(timer);
    } else {
      console.warn('⚠️ Cannot calculate ETA - liveTask or handyman missing:', { liveTask: !!liveTask, handyman: !!displayHandyman });
    }
  }, [liveTask, displayHandyman, currentHandymanPos, socketHandymanLocation]);

  // -------------------------------------------------------------
  // DISTANCE MATRIX CALCULATION
  // -------------------------------------------------------------
  
  // Haversine formula for fallback distance calculation
  const calculateHaversineDistance = (pos1, pos2) => {
    const R = 6371; // Earth's radius in km
    const dLat = ((pos2.lat - pos1.lat) * Math.PI) / 180;
    const dLng = ((pos2.lng - pos1.lng) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((pos1.lat * Math.PI) / 180) *
        Math.cos((pos2.lat * Math.PI) / 180) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Distance in km
  };

  const calculateETA = (handymanLocation) => {
    console.log('📍 Calculating ETA with handyman location:', handymanLocation);
    console.log('📍 Customer position:', customerPos);
    
    // Fallback: use haversine formula if Google API not available or fails
    const fallbackCalculation = () => {
      const distKm = calculateHaversineDistance(handymanLocation, customerPos);
      console.log('📏 Calculated distance (fallback):', distKm, 'km');
      // Assume average speed of 30 km/h in city traffic
      const etaMinutes = Math.round((distKm / 30) * 60);
      setDistance(`${distKm.toFixed(1)} km`);
      if (etaMinutes < 60) {
        setEta(`${etaMinutes} mins`);
      } else {
        const hours = Math.floor(etaMinutes / 60);
        const mins = etaMinutes % 60;
        setEta(`${hours} hr ${mins} mins`);
      }
      console.log('⏱️ Set ETA (fallback):', etaMinutes, 'mins, Distance:', distKm.toFixed(1), 'km');
    };

    // Try Google Distance Matrix first
    if (!window.google?.maps?.DistanceMatrixService) {
      console.log("Distance Matrix API not available, using fallback");
      fallbackCalculation();
      return;
    }

    try {
      const service = new window.google.maps.DistanceMatrixService();

      service.getDistanceMatrix(
        {
          origins: [handymanLocation],
          destinations: [customerPos],
          travelMode: "DRIVING",
        },
        (response, status) => {
          if (status === "OK" && response?.rows?.[0]?.elements?.[0]?.status === "OK") {
            const result = response.rows[0].elements[0];
            console.log('✅ Distance Matrix API Success:', result);
            setEta(result.duration.text);
            setDistance(result.distance.text);
          } else {
            console.log("Distance Matrix failed, using fallback. Status:", status);
            fallbackCalculation();
          }
        }
      );
    } catch (err) {
      console.error("Distance Matrix error:", err);
      fallbackCalculation();
    }
  };

  // -------------------------------------------------------------
  // UPDATE MARKER BASED ON STATUS
  // -------------------------------------------------------------
  useEffect(() => {
    if (!handymanMarkerRef.current || !window.google?.maps?.marker?.PinElement) return;

    const { PinElement } = window.google.maps.marker;

    let pinColor = "#34A853"; // Default green

    switch (status) {
      case "assigned":
      case "handyman_selected":
        pinColor = "#3B82F6"; // Blue - assigned
        break;
      case "on_the_way":
        pinColor = "#34A853"; // Green - traveling
        break;
      case "arrived":
        pinColor = "#FBBC05"; // Yellow - arrived
        break;
      case "work_started":
        pinColor = "#6366F1"; // Indigo - working
        break;
      case "in_progress":
        pinColor = "#10B981"; // Emerald - awaiting confirmation
        break;
      case "completed":
        pinColor = "#4285F4"; // Blue - done
        break;
      case "cancelled":
        pinColor = "#9CA3AF"; // Gray - cancelled
        break;
      default:
        pinColor = "#34A853"; // Green
    }

    const newPinElement = new PinElement({
      background: pinColor,
      borderColor: pinColor,
      glyphColor: "#FFFFFF",
    });

    // Update the marker content
    handymanMarkerRef.current.content = newPinElement.element;

  }, [status]);

  // -------------------------------------------------------------
  // STATUS BADGE - Updated for new job flow
  // Flow: assigned → on_the_way → arrived → work_started → in_progress → completed
  // -------------------------------------------------------------
  const statusLabel = {
    pending_offers: "Finding Handyman",
    assigned: "Handyman Assigned",
    handyman_selected: "Handyman Assigned",
    on_the_way: "On The Way",
    arrived: "Arrived",
    work_started: "Work In Progress",
    in_progress: "Awaiting Approval",
    approved: "Waiting for payment confirmation",
    payment_received: "Payment Received - Confirm & Rate",
    completed: "Completed",
    cancelled: "Cancelled",
  };

  const statusColor = {
    pending_offers: "bg-orange-100 text-orange-700",
    assigned: "bg-blue-100 text-blue-700",
    handyman_selected: "bg-blue-100 text-blue-700",
    on_the_way: "bg-yellow-100 text-yellow-700",
    arrived: "bg-purple-100 text-purple-700",
    work_started: "bg-indigo-100 text-indigo-700",
    in_progress: "bg-green-100 text-green-700",
    approved: "bg-emerald-100 text-emerald-700",
    payment_received: "bg-emerald-100 text-emerald-700",
    completed: "bg-gray-300 text-gray-600",
    cancelled: "bg-red-100 text-red-700",
  };

  // Check if required data is available (after all hooks)
  if (isLoadingTask || !liveTask) {
    return (
      <div className="w-full h-[500px] flex items-center justify-center bg-gray-100">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        <span>{taskLoadError || "Loading tracking data..."}</span>
      </div>
    );
  }

  if (!displayHandyman) {
    return (
      <div className="w-full h-[500px] flex items-center justify-center bg-gray-100">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        <span>{taskLoadError || "Loading handyman details..."}</span>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="w-full h-[500px] flex items-center justify-center bg-gray-100">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        <span>Loading map...</span>
      </div>
    );
  }

  return (
    <div className="relative w-full h-[500px]">
      {/* MAP */}
      <GoogleMap
        center={customerPos}
        zoom={14}
        onLoad={(map) => (mapRef.current = map)}
        mapContainerStyle={{ width: "100%", height: "100%" }}
        options={{
          mapId: "LIVE_TRACKING_MAP", // Optional: for custom map styling
        }}
      ></GoogleMap>

      {/* CONNECTION STATUS */}
      {!isConnected && (
        <div className="absolute top-4 right-4 bg-red-500 text-white px-3 py-2 rounded-lg shadow-lg text-sm font-medium">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
            Reconnecting...
          </div>
        </div>
      )}

      {/* STATUS CARD */}
      <div className="absolute top-4 left-4 bg-white shadow-lg rounded-xl p-4 min-w-[200px]">
        <div className="flex items-center gap-2 mb-2">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
            <span className="text-xs text-gray-600">You</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            <span className="text-xs text-gray-600">{displayHandyman?.name || "Handyman"}</span>
          </div>
        </div>

        <h3 className="font-semibold text-lg">{statusLabel[status]}</h3>
        <p className="text-gray-600 mt-1 flex items-center gap-1">
          <Clock className="w-4 h-4" />
          ETA: <strong>{eta || "Calculating..."}</strong>
        </p>
        <p className="text-gray-600 flex items-center gap-1">
          <MapPin className="w-4 h-4" />
          Distance: <strong>{distance || "Calculating..."}</strong>
        </p>

        {isConnected && (
          <div className="mt-2 flex items-center gap-1 text-xs text-green-600">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            Live Tracking Active
          </div>
        )}

        <button
          type="button"
          onClick={() => navigate("/customer/dashboard")}
          className="mt-3 w-full border border-gray-300 text-gray-800 py-2 px-2 rounded-lg flex justify-center items-center gap-2 hover:bg-gray-50 transition-colors"
        >
          <Navigation className="w-4 h-4" /> Customer Dashboard
        </button>
      </div>

      {/* BOTTOM HANDYMAN CARD */}
      <div className="absolute bottom-0 left-0 right-0 p-3">
        <div className="bg-white rounded-xl shadow-lg p-4">
          <div className="flex items-center gap-3">
            <img
              src={displayHandyman?.profileImage || "https://via.placeholder.com/56"}
              className="w-14 h-14 rounded-full border-2 border-green-500"
              alt="handyman"
            />
            <div className="flex-1">
              <h2 className="text-lg font-semibold">{displayHandyman?.name || "Handyman"}</h2>
              <div className="flex items-center gap-2 mt-1">
                <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                <span className="text-sm text-gray-600">
                  {displayHandyman?.rating} ({displayHandyman?.ratingCount}+)
                </span>
              </div>
            </div>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColor[status]}`}>
              {statusLabel[status]}
            </span>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 mt-4">
            <button
              onClick={() => {
                if (displayHandyman?.phoneNumber) {
                  window.location.href = `tel:${displayHandyman.phoneNumber}`;
                } else if (displayHandyman?.phone) {
                  window.location.href = `tel:${displayHandyman.phone}`;
                }
              }}
              className="flex-1 bg-blue-600 text-white py-2 rounded-lg flex justify-center items-center gap-2 hover:bg-blue-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              disabled={!displayHandyman?.phoneNumber && !displayHandyman?.phone}
            >
              <Phone className="w-4 h-4" /> Call
            </button>
            <button
              onClick={() => setIsChatVisible(true)}
              className="flex-1 border border-gray-300 text-gray-800 py-2 rounded-lg flex justify-center items-center gap-2 hover:bg-gray-50 transition-colors"
            >
              <MessageCircle className="w-4 h-4" /> Chat
            </button>
          </div>

          {/* Mark work done - Only show when handyman completed work (in_progress = awaiting confirmation) */}
          {status === "in_progress" && (liveTask?.awaitingConfirmation || !liveTask) && (
            <button
              onClick={() => setShowApproveForm(true)}
              className="mt-3 w-full bg-green-600 text-white py-2 rounded-lg flex justify-center items-center gap-2 hover:bg-green-700 transition-colors"
            >
              <CheckCircle className="w-5 h-5" /> Approve Work
            </button>
          )}

          {status === "approved" && (
            <div className="mt-3">
              <button
                onClick={() => setShowCompletionForm(true)}
                disabled={!paymentConfirmed}
                className="w-full bg-blue-600 text-white py-2 rounded-lg flex justify-center items-center gap-2 hover:bg-blue-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <CheckCircle className="w-5 h-5" /> Rate Handyman
              </button>

              {!paymentConfirmed && (
                <div className="mt-2 text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                  Waiting for handyman to confirm payment received.
                </div>
              )}
            </div>
          )}

          {status === "payment_received" && (
            <button
              onClick={() => setShowCompletionForm(true)}
              className="mt-3 w-full bg-blue-600 text-white py-2 rounded-lg flex justify-center items-center gap-2 hover:bg-blue-700 transition-colors"
            >
              <CheckCircle className="w-5 h-5" /> Rate Handyman
            </button>
          )}

          {/* Dispute & Cancel */}
          {!['completed', 'cancelled', 'disputed'].includes(status) && (
            <div className="flex gap-2 mt-3">
              {['in_progress', 'approved'].includes(status) && (
                <button
                  onClick={() => setShowDisputeDialog(true)}
                  className="flex-1 bg-orange-500 text-white py-2 rounded-lg flex justify-center items-center gap-2 hover:bg-orange-600 transition-colors"
                >
                  <AlertTriangle className="w-5 h-5" /> Dispute
                </button>
              )}
              {!['work_started', 'in_progress', 'approved', 'payment_received'].includes(status) && (
                <button
                  onClick={() => setShowCancelForm(true)}
                  className="flex-1 bg-red-500 text-white py-2 rounded-lg flex justify-center items-center gap-2 hover:bg-red-600 transition-colors"
                >
                  <XCircle className="w-5 h-5" /> Cancel
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {showApproveForm && (
        <div className="absolute inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-4 relative">
            <button
              onClick={() => setShowApproveForm(false)}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-800"
            >
              <XCircle className="w-5 h-5" />
            </button>
            <h3 className="text-base font-semibold text-gray-900 mb-1">
              Approve completed work
            </h3>
            <p className="text-xs text-gray-600 mb-3">
              Review the handyman's submitted details and approve the completion.
            </p>

            {liveTask?.finalPrice && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3">
                <p className="text-xs font-semibold text-green-900 mb-1">Payment Required</p>
                <p className="text-lg font-bold text-green-700">
                  PKR {Number(liveTask.finalPrice).toLocaleString()}
                </p>
                <p className="text-xs text-green-700 mt-1">
                  Please pay this amount to the handyman before approving.
                </p>
              </div>
            )}

            <div className="space-y-3">
              {(workDescription || (workPictures && workPictures.length) || workVideo) && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-3 max-h-[240px] overflow-auto">
                  {workDescription && (
                    <div>
                      <p className="text-xs font-semibold text-gray-700">Work description</p>
                      <p className="text-xs text-gray-800 mt-1 whitespace-pre-wrap">
                        {workDescription}
                      </p>
                    </div>
                  )}

                  {Array.isArray(workPictures) && workPictures.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-gray-700 mb-2">Pictures</p>
                      <div className="grid grid-cols-3 gap-2">
                        {workPictures.slice(0, 6).map((src, idx) => (
                          <img
                            key={`${idx}-${String(src)}`}
                            src={src}
                            alt={`work-${idx}`}
                            className="w-full h-14 object-cover rounded-md border border-gray-200"
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                            }}
                          />
                        ))}
                      </div>
                      {workPictures.length > 6 && (
                        <p className="text-xs text-gray-600 mt-2">
                          +{workPictures.length - 6} more
                        </p>
                      )}
                    </div>
                  )}

                  {workVideo && (
                    <div>
                      <p className="text-xs font-semibold text-gray-700 mb-2">Video</p>
                      <video
                        src={typeof workVideo === "string" ? workVideo : undefined}
                        controls
                        className="w-full rounded-md border border-gray-200"
                      />
                    </div>
                  )}
                </div>
              )}

              {approveError && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  {approveError}
                </div>
              )}

              <button
                onClick={approveCompletion}
                disabled={isApprovingCompletion}
                className="w-full bg-green-600 text-white py-2 rounded-lg flex justify-center items-center gap-2 hover:bg-green-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isApprovingCompletion && (
                  <Loader2 className="w-4 h-4 animate-spin" />
                )}
                <CheckCircle className="w-5 h-5" />
                {isApprovingCompletion ? "Approving..." : "Approve"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCompletionForm && (
        <div className="absolute inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 relative">
            <button
              onClick={() => setShowCompletionForm(false)}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-800"
            >
              <XCircle className="w-5 h-5" />
            </button>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Confirm completion
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Rate and review the handyman, then confirm completion.
            </p>

            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-gray-800 mb-2">Rating</p>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((value) => (
                    <button
                      key={value}
                      onClick={() => setRating(value)}
                      className={`p-2 rounded-lg border transition-colors ${
                        rating >= value
                          ? "bg-amber-50 border-amber-300 text-amber-600"
                          : "bg-gray-50 border-gray-200 text-gray-500"
                      }`}
                      aria-label={`Rate ${value} star${value > 1 ? "s" : ""}`}
                    >
                      <Star
                        className={`w-5 h-5 ${
                          rating >= value ? "fill-amber-400 text-amber-400" : ""
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-800 mb-2 block">
                  Review (optional)
                </label>
                <textarea
                  value={review}
                  onChange={(e) => setReview(e.target.value)}
                  rows={3}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Share your experience..."
                />
              </div>

              {completionError && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  {completionError}
                </div>
              )}

              <button
                onClick={submitCompletion}
                disabled={isSubmittingCompletion}
                className="w-full bg-blue-600 text-white py-2 rounded-lg flex justify-center items-center gap-2 hover:bg-blue-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isSubmittingCompletion && (
                  <Loader2 className="w-4 h-4 animate-spin" />
                )}
                <CheckCircle className="w-5 h-5" />
                {isSubmittingCompletion ? "Submitting..." : "Confirm completion"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCancelForm && (
        <div className="absolute inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 relative">
            <button
              onClick={() => setShowCancelForm(false)}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-800"
            >
              <XCircle className="w-5 h-5" />
            </button>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Cancel service request
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Please share a short reason. A 10% penalty may apply if work was already assigned or in progress.
            </p>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-800 mb-2 block">
                  Reason for cancellation
                </label>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  rows={3}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="E.g. schedule changed, found alternative, etc."
                />
              </div>

              {cancelError && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  {cancelError}
                </div>
              )}

              <button
                onClick={submitCancellation}
                disabled={isCancelling}
                className="w-full bg-red-600 text-white py-2 rounded-lg flex justify-center items-center gap-2 hover:bg-red-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isCancelling && <Loader2 className="w-4 h-4 animate-spin" />}
                <XCircle className="w-5 h-5" />
                {isCancelling ? "Cancelling..." : "Confirm cancellation"}
              </button>
            </div>
          </div>
        </div>
      )}

      {isChatVisible && displayHandyman?._id && (
        <Chat
          handymanName={displayHandyman?.name}
          handymanAvatar={displayHandyman?.profileImage}
          isLive={true}
          participantId={displayHandyman._id}
          onCall={() => {
            if (displayHandyman?.phoneNumber) {
              window.location.href = `tel:${displayHandyman.phoneNumber}`;
            } else if (displayHandyman?.phone) {
              window.location.href = `tel:${displayHandyman.phone}`;
            }
          }}
          onEndChat={() => setIsChatVisible(false)}
        />
      )}

      {/* Dispute Dialog */}
      <DisputeDialog
        isOpen={showDisputeDialog}
        onClose={(disputeRaised) => {
          setShowDisputeDialog(false);
          if (disputeRaised) {
            setStatus("disputed");
            navigate("/customer/jobs");
          }
        }}
        taskId={taskId}
        userType="customer"
      />
    </div>
  );
};

export default LiveHandymanTracking;